import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Avatar } from "../avatar/avatar";
import { getInitialsFromText } from "../avatar/avatar-initials";
import { getDataTestId } from "../functions/data-test-ids";
import { useIsHovered } from "../hooks/useIsHovered";
import { AppFont, FontAwesome } from "../icons/icons";
import {
    BadgeAlignment,
    BadgeStyle,
    CardIconType,
    CardLayout
} from "./card-constants";

import "./cards.css";

function CardIcon(props) {
    const { Item, DataTestId } = props;
    if (!Item || !Item.IconType || Item.IconType === CardIconType.None) {
        return null;
    }

    if (Item.IconType === CardIconType.Custom) {
        return Item.IconNode || null;
    }

    // Both avatar branches size themselves from .eone-ui-card-icon rather than passing a Size, so
    // grid and list cards keep their existing 36px / 40px boxes.
    if (Item.IconType === CardIconType.Avatar) {
        const seed = Item.SeedValue || Item.InitialsValue;
        // An item with no display name - an invited user who has not set one yet - still has a seed,
        // and an email reads fine as initials, so fall back to it rather than leaving the box empty.
        const initials = getInitialsFromText(Item.InitialsValue) || getInitialsFromText(seed);
        // Nothing to draw either way: keep the neutral empty box so the card layout holds.
        if (!Item.Icon && !initials) {
            return <div className="eone-ui-card-icon" />;
        }
        return (
            <Avatar
                Src={Item.Icon || null}
                Initials={initials}
                Seed={seed}
                Background={Item.IconColor}
                Class="eone-ui-card-icon"
                DataTestId={DataTestId} />
        );
    }

    if (Item.IconType === CardIconType.Initials) {
        const initials = getInitialsFromText(Item.Icon);
        if (!initials) return null;
        return (
            <Avatar
                Src={null}
                Initials={initials}
                Seed={Item.SeedValue || Item.Icon}
                Background={Item.IconColor}
                Class="eone-ui-card-icon"
                DataTestId={DataTestId} />
        );
    }

    if (!Item.Icon) return null;

    const style = Item.IconColor ? { background: Item.IconColor, color: "var(--text-on-color-a)" } : undefined;
    const className = Item.IconColor ? "eone-ui-card-icon eone-ui-card-icon-filled" : "eone-ui-card-icon";

    if (Item.IconType === CardIconType.AppFontIcon) {
        return (
            <div className={className} style={style}>
                <AppFont Icon={Item.Icon} />
            </div>
        );
    }

    return (
        <div className={className} style={style}>
            <FontAwesome Icon={Item.Icon} />
        </div>
    );
}

function CardBadge(props) {
    const { Badge } = props;
    if (!Badge) return null;
    if (Badge.Hidden) return null;

    const getBadgeClass = () => {
        let className = "eone-ui-card-badge";
        switch (Badge.Style) {
            case BadgeStyle.Primary:
                className += " eone-ui-card-badge-primary";
                break;
            case BadgeStyle.Secondary:
                className += " eone-ui-card-badge-secondary";
                break;
            case BadgeStyle.Danger:
                className += " eone-ui-card-badge-danger";
                break;
            case BadgeStyle.Warning:
                className += " eone-ui-card-badge-warning";
                break;
            default:
                className += " eone-ui-card-badge-default";
                break;
        }
        if (Badge.Alignment === BadgeAlignment.Right) {
            className += " eone-ui-card-badge-align-right";
        }
        if (Badge.Large) {
            className += " eone-ui-card-badge-large";
        }
        return className;
    };

    const renderBadgeIcon = () => {
        if (!Badge.Icon) return null;
        if (Badge.IconType === CardIconType.AppFontIcon) {
            return <AppFont Icon={Badge.Icon} />;
        }
        return <FontAwesomeIcon icon={Badge.Icon} />;
    };

    return (
        <span className={getBadgeClass()} title={Badge.Title}>
            {renderBadgeIcon()}
            <span className="eone-ui-card-badge-label">{Badge.Label}</span>
        </span>
    );
}

function CardActionButton(props) {
    const { Action, ItemKey, DataTestId } = props;

    const click = (event) => {
        event.stopPropagation();
        if (typeof Action.OnClick === "function") {
            Action.OnClick(ItemKey);
        }
    };

    const stopDoubleClick = (event) => {
        event.stopPropagation();
    };

    let className = "eone-ui-card-action";
    if (Action.Cancel) className += " eone-ui-card-action-danger";

    return (
        <button
            type="button"
            className={className}
            title={Action.Title || Action.Label}
            aria-label={Action.Title || Action.Label}
            onClick={click}
            onDoubleClick={stopDoubleClick}
            data-testid={getDataTestId(DataTestId, "action", Action.Label || Action.Title)}>
            <FontAwesomeIcon icon={Action.Icon} />
        </button>
    );
}

function isPresent(value) {
    return value !== undefined && value !== null && value !== "";
}

function Card(props) {
    const layout = props.Layout || CardLayout.Grid;
    const item = props.Item || {};
    const itemKey = item.Key;

    const [hoverRef, isHovered] = useIsHovered();

    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: itemKey,
        disabled: !props.Reorderable,
        transition: { duration: 25, easing: "cubic-bezier(1, 1, 0.5, 1)" }
    });

    const click = () => {
        if (typeof props.OnSelect === "function") {
            props.OnSelect(itemKey);
        }
    };

    const doubleClick = () => {
        if (typeof props.OnDoubleClick === "function") {
            props.OnDoubleClick(itemKey);
        }
    };

    const onKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            click();
        }
    };

    const getCardClass = () => {
        let className = "eone-ui-card";
        className += layout === CardLayout.List ? " eone-ui-card-list-item" : " eone-ui-card-grid-item";
        if (props.Selected) className += " eone-ui-card-selected";
        if (typeof props.OnSelect === "function") className += " eone-ui-card-clickable";
        if (props.Reorderable) className += " eone-ui-card-reorderable";
        if (props.Reorderable && transform) className += " eone-ui-card-draggable";
        if (props.Reorderable && isDragging) className += " eone-ui-card-dragging";
        return className;
    };

    const getCardStyle = () => {
        const rowStyle = props.RowStyle;
        if (props.Reorderable && transform) {
            return {
                ...(rowStyle || {}),
                transition,
                transform: CSS.Translate.toString(transform)
            };
        }
        return rowStyle || undefined;
    };

    const setRef = (node) => {
        hoverRef(node);
        if (props.Reorderable) {
            setNodeRef(node);
        }
    };

    const dragProps = props.Reorderable ? { ...attributes, ...listeners } : {};
    const isClickable = typeof props.OnSelect === "function";

    const renderActions = () => {
        if (!item.Actions || item.Actions.length === 0) return null;
        const visible = item.Actions.filter(action => !action.Hidden);
        if (visible.length === 0) return null;
        const reveal = isHovered || props.Selected;
        const actionsClass = `eone-ui-card-actions${reveal ? " eone-ui-card-actions-visible" : ""}`;
        return (
            <div className={actionsClass}>
                {visible.map((action, index) => (
                    <CardActionButton
                        key={index}
                        Action={action}
                        ItemKey={itemKey}
                        DataTestId={props.DataTestId} />
                ))}
            </div>
        );
    };

    if (layout === CardLayout.List) {
        const visibleBadges = (item.Badges || []).filter(b => b && !b.Hidden && (b.AlwaysShow || isPresent(b.Label)));

        return (
            <div
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                className={getCardClass()}
                ref={setRef}
                style={getCardStyle()}
                onClick={isClickable ? click : undefined}
                onDoubleClick={props.OnDoubleClick ? doubleClick : undefined}
                onKeyDown={isClickable ? onKeyDown : undefined}
                data-testid={getDataTestId(props.DataTestId, "card", itemKey)}
                {...dragProps}>
                <CardIcon Item={item} DataTestId={getDataTestId(props.DataTestId, "card", itemKey)} />
                <div className="eone-ui-card-list-body">
                    {isPresent(item.Title)
                        ? (
                            <div className="eone-ui-card-title">
                                <span className="eone-ui-card-title-name">{item.Title}</span>
                                {isPresent(item.TitleVersion)
                                    ? (
                                        <span className="eone-ui-card-title-version">
                                            <span className="eone-ui-card-title-version-dot">•</span>
                                            <span>{item.TitleVersion}</span>
                                        </span>
                                    )
                                    : null}
                            </div>
                        )
                        : null}
                    {isPresent(item.Subtitle)
                        ? <div className="eone-ui-card-subtitle">{item.Subtitle}</div>
                        : null}
                    {visibleBadges.length > 0
                        ? (
                            <div className="eone-ui-card-list-badges">
                                {visibleBadges.map((badge, index) => (
                                    <CardBadge key={index} Badge={badge} />
                                ))}
                            </div>
                        )
                        : null}
                </div>
                {item.Trailing
                    ? <div className="eone-ui-card-trailing">{item.Trailing}</div>
                    : null}
                {renderActions()}
            </div>
        );
    }

    const allBadges = (item.Badges || []).filter(b => b && !b.Hidden && (b.AlwaysShow || isPresent(b.Label)));
    const leftBadges = allBadges.filter(b => (b.Alignment || BadgeAlignment.Left) === BadgeAlignment.Left);
    const rightBadges = allBadges.filter(b => b.Alignment === BadgeAlignment.Right);
    const showFooter = leftBadges.length > 0 || rightBadges.length > 0;

    return (
        <div
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            className={getCardClass()}
            ref={setRef}
            style={getCardStyle()}
            onClick={isClickable ? click : undefined}
            onDoubleClick={props.OnDoubleClick ? doubleClick : undefined}
            onKeyDown={isClickable ? onKeyDown : undefined}
            data-testid={getDataTestId(props.DataTestId, "card", itemKey)}
            {...dragProps}>
            <div className="eone-ui-card-header">
                <CardIcon Item={item} DataTestId={getDataTestId(props.DataTestId, "card", itemKey)} />
                <div className="eone-ui-card-header-text">
                    {isPresent(item.Title)
                        ? (
                            <div className="eone-ui-card-title">
                                <span className="eone-ui-card-title-name">{item.Title}</span>
                                {isPresent(item.TitleVersion)
                                    ? (
                                        <span className="eone-ui-card-title-version">
                                            <span className="eone-ui-card-title-version-dot">•</span>
                                            <span>{item.TitleVersion}</span>
                                        </span>
                                    )
                                    : null}
                            </div>
                        )
                        : null}
                    {isPresent(item.Subtitle)
                        ? <div className="eone-ui-card-subtitle">{item.Subtitle}</div>
                        : null}
                </div>
                {renderActions()}
            </div>
            {isPresent(item.Body)
                ? (
                    <div className="eone-ui-card-body">
                        {typeof item.Body === "string"
                            ? <div className="eone-ui-card-body-text">{item.Body}</div>
                            : item.Body}
                    </div>
                )
                : null}
            {showFooter
                ? (
                    <div className="eone-ui-card-footer">
                        <div className="eone-ui-card-badges-left">
                            {leftBadges.map((badge, index) => (
                                <CardBadge key={index} Badge={badge} />
                            ))}
                        </div>
                        <div className="eone-ui-card-badges-right">
                            {rightBadges.map((badge, index) => (
                                <CardBadge key={index} Badge={badge} />
                            ))}
                        </div>
                    </div>
                )
                : null}
        </div>
    );
}

export { Card };
